/**
 * Corporate Theme Configuration for Premium Redesign
 * 
 * This theme establishes a premium, corporate B2B service platform identity
 * with a restricted color palette, structured typography, and minimal design.
 * 
 * Design Principles:
 * - Corporate Authority: Clean, structured layouts
 * - Visual Minimalism: Strong white space, limited colors
 * - NO gradients, NO flashy animations
 * - Maximum 2 font families
 */

export interface CorporateTheme {
  colors: {
    primary: string;
    background: string;
    surface: string;
    accent: string;
    text: {
      primary: string;
      secondary: string;
      inverse: string;
    };
    border: string;
  };
  typography: {
    fontFamily: {
      primary: string;
    };
    scale: {
      h1: string;
      h2: string;
      h3: string;
      body: string;
      small: string;
    };
    weight: {
      regular: number;
      semibold: number;
      bold: number;
    };
    lineHeight: {
      tight: number;
      normal: number;
      relaxed: number;
    };
  };
  spacing: {
    section: string;
    container: string;
    element: string;
    component: string;
  };
  borderRadius: {
    small: string;
    medium: string;
    large: string;
  };
  shadows: {
    subtle: string;
    medium: string;
    strong: string;
  };
  breakpoints: {
    mobile: string;
    tablet: string;
    desktop: string;
    maxWidth: string;
  };
}

export const corporateTheme: CorporateTheme = {
  colors: {
    primary: '#1a2332',      // Deep navy
    background: '#FFFFFF',    // Pure white
    surface: '#f5f5f5',      // Soft gray
    accent: '#1a2332',       // Same as primary (no bright accent)
    text: {
      primary: '#1a2332',
      secondary: '#6b7280',
      inverse: '#FFFFFF',
    },
    border: '#e5e7eb',
  },
  typography: {
    fontFamily: {
      primary: 'Inter, system-ui, sans-serif',
    },
    scale: {
      h1: '56px',            // 3.5rem
      h2: '40px',            // 2.5rem
      h3: '24px',            // 1.5rem
      body: '16px',          // 1rem
      small: '14px',         // 0.875rem
    },
    weight: {
      regular: 400,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.1,
      normal: 1.5,
      relaxed: 1.6,
    },
  },
  spacing: {
    section: '96px',         // 6rem
    container: '64px',       // 4rem
    element: '32px',         // 2rem
    component: '24px',       // 1.5rem
  },
  borderRadius: {
    small: '8px',
    medium: '12px',
    large: '16px',
  },
  shadows: {
    subtle: '0 2px 8px rgba(0, 0, 0, 0.04)',
    medium: '0 4px 12px rgba(0, 0, 0, 0.08)',
    strong: '0 8px 24px rgba(0, 0, 0, 0.12)',
  },
  breakpoints: {
    mobile: '0px - 767px',
    tablet: '768px - 1023px',
    desktop: '1024px+',
    maxWidth: '1280px',
  },
};

/**
 * Responsive typography scale for different breakpoints
 */
export const responsiveTypography = {
  mobile: {
    h1: '36px',
    h2: '28px',
    h3: '20px',
    body: '16px',
  },
  tablet: {
    h1: '48px',
    h2: '36px',
    h3: '22px',
    body: '16px',
  },
  desktop: {
    h1: '56px',
    h2: '40px',
    h3: '24px',
    body: '16px',
  },
};

/**
 * Responsive spacing scale for different breakpoints
 */
export const responsiveSpacing = {
  mobile: {
    section: '64px',
    container: '24px',
  },
  tablet: {
    section: '80px',
    container: '40px',
  },
  desktop: {
    section: '96px',
    container: '64px',
  },
};

/**
 * Responsive grid columns for different breakpoints
 */
export const responsiveGrid = {
  mobile: {
    columns: 1,
  },
  tablet: {
    columns: 2,
  },
  desktop: {
    columns: 4,
  },
};
